import { getOrCreateMainLogger } from "../logger/main";
import { accessDenied } from "../util";
import { permissionsManager } from "./PermissionsManager";
import { windowsIndex } from "./mainStore";
import { ipcMain, IpcMainEvent, WebContents } from "electron";
import { WebContentsId, IABPublish, IABSubscribe, IABSubscribeResponse, IABUnsubscribe } from "../types/types";

const logger = getOrCreateMainLogger();

/**
 * IAB relays messages sent through the InterApplicationBus interface in renderer processes. The
 * main process does not interact with IAB except to remove subscriptions when windows close.
 */
class IAB {
	// Lookup listenerID's by destination
	subscribers: {
		[destination: string]: {
			[listenerID: string]: WebContents;
		};
	} = {};

	constructor() {
		this.setupListeners();
	}

	/**
	 * Removes all subscriptions for a window. This happens on window close.
	 */
	unsubscribeWebContentsId(closingWebContentsId: WebContentsId) {
		Object.entries(this.subscribers).forEach(([destination, listeners]) => {
			// For a destination, delete any listeners on the closing WebContents id
			Object.entries(listeners).forEach(([listenerID, webContents]) => {
				if (webContents.id === closingWebContentsId) {
					delete listeners[closingWebContentsId];
				}
			});
			// Remove a destination if it has no listeners
			if (Object.keys(listeners).length === 0) {
				delete this.subscribers[destination];
			}
		});
	}

	/**
	 *
	 */
	onIABSubscribe(event: IpcMainEvent, arg: IABSubscribe) {
		const webContentsId = event.sender.id;
		const { mainWindow } = windowsIndex.byWebContentsId[webContentsId] ?? {};
		if (!mainWindow) {
			logger.error(`onIABPublish() WebContents ${webContentsId} not found in windowsIndex.`);
			return;
		}
		const { listenerID, source } = arg;
		const permission = "InterApplicationBus.subscribe";
		if (!permissionsManager.checkPermission(mainWindow, permission)) {
			try {
				event.sender.send("subscribeResponseError", accessDenied(permission));
			} catch (error) {
				logger.error("Error sending subscribeResponseError", error);
			}
			logger.info(`Subscription to electron event id ${webContentsId} denied. Does not have the permission.`);
		}

		// Add the listener ID to the source (alongside any previous listener IDs).
		this.subscribers[source] = {
			...this.subscribers[source],
			[listenerID]: event.sender,
		};
		logger.debug(`Subscribing to ${source} from electron event id ${webContentsId}`);
	}

	/**
	 *
	 */
	onIABUnsubscribe(event: IpcMainEvent, arg: IABUnsubscribe) {
		const webContentsId = event.sender.id;
		const { mainWindow } = windowsIndex.byWebContentsId[webContentsId] ?? {};
		if (!mainWindow) {
			logger.error(`onIABPublish() WebContents ${webContentsId} not found in windowsIndex.`);
			return;
		}
		const permission = "InterApplicationBus.unsubscribe";
		if (!permissionsManager.checkPermission(mainWindow, permission)) {
			try {
				event.sender.send("unSubscribeResponseError", accessDenied(permission));
			} catch (err) {
				logger.error("Error sending unSubscribeResponseError", err);
			}
			logger.debug(`Unsubscribe to electron event id ${webContentsId} denied. Does not have the permission.`);
			return;
		}
		const { listenerID } = arg;
		Object.entries(this.subscribers).forEach(([destination, listeners]) => {
			// Remove the unsubscribed listener ID
			if (listeners[listenerID]) {
				logger.debug(`Unsubscribing to ${destination} from electron event id ${webContentsId}`);
				delete listeners[listenerID];
			}
			// If destination has no more listeners then remove the destination
			if (Object.keys(listeners).length === 0) {
				delete this.subscribers[destination];
			}
		});
	}

	/**
	 * Recieves a message from one of the render processes that is destined for another.
	 * A subscribeResponse is sent to each of registered subscriber for the destination windowName.
	 */
	onIABPublish(event: IpcMainEvent, arg: IABPublish) {
		const webContentsId = event.sender.id;
		const { mainWindow } = windowsIndex.byWebContentsId[webContentsId] ?? {};
		if (!mainWindow) {
			logger.error(`onIABPublish() WebContents ${webContentsId} not found in windowsIndex.`);
			return;
		}
		const permission = "InterApplicationBus.publish";

		// return error if publishing not permitted within permissions
		if (!permissionsManager.checkPermission(mainWindow, permission)) {
			try {
				event.sender.send("publishResponseError", accessDenied(permission));
			} catch (error) {
				logger.error("Error sending permission-failure publishResponseError", error);
			}
		} else {
			const { destination, data } = arg;
			const subscribers = this.subscribers[destination] || {};
			Object.entries(subscribers).forEach(([listenerID, webContents]) => {
				try {
					// It's possible for a remote window to close while a publish is in transit. In
					// this situation, isDestroyed() will be true
					if (webContents.isDestroyed()) return;
					webContents.send("subscribeResponse", {
						data,
						listenerID,
					} as IABSubscribeResponse);
				} catch (error) {
					logger.error("Error sending subscribeResponse", error, data);
				}
			});
		}
	}

	/**
	 * Pub/Sub for Finsemble window communications on the "IAB" transport.
	 */
	setupListeners() {
		ipcMain.on("e2o.iab.subscribe", this.onIABSubscribe.bind(this));
		ipcMain.on("e2o.iab.unsubscribe", this.onIABUnsubscribe.bind(this));
		ipcMain.on("e2o.iab.publish", this.onIABPublish.bind(this));
	}
}

export const iab = new IAB();
